import React, { useCallback } from "react";
import { View, ScrollView, Pressable } from "react-native";
import { Plus, Trash2 } from "lucide-react-native";
import TimeInput from "./time-input";
import { ITimeSlot, ISchedule } from "../../../../types/agent-settings";
import StyledText from "../../../common/ui/styled-text";
import { useViewport } from "../../../../hooks/useViewport";
import {
  hasOverlappingSlots,
  isTimeInAllowedBounds,
} from "../../../../utils/setting-helpers";

interface HoursSelectorProps {
  schedule: ISchedule;
  isEditing: boolean;
  onUpdateSchedule: (newSchedule: ISchedule) => void;
  validationError?: string;
  disabled?: boolean;
  showValidation?: boolean;
  closedText?: string;
  displayEmptyDays?: boolean;
  allowedScheduleBounds?: ISchedule | null;
  allowOverlappingSlots?: boolean;
  readonly?: boolean;
  // Optional render prop to inject extra inline per-slot fields (e.g. prep times)
  renderSlotExtras?: (args: {
    slot: ITimeSlot & { [key: string]: any };
    day: keyof ISchedule;
    index: number;
    updateSlotField: (field: string, value: any) => void;
    disabled: boolean;
    isEditing: boolean;
  }) => React.ReactNode;
}

export const is24Hours = (slot: ITimeSlot) =>
  slot.open && slot.close && slot.open === slot.close;

const formatTime = (time: string | null) => {
  if (!time) return "-";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${ampm}`;
};

const formatDay = (day: string) => {
  return day.charAt(0).toUpperCase() + day.slice(1);
};

const formatDayShort = (day: string) => {
  return day.charAt(0).toUpperCase() + day.slice(1, 3) + ".";
};

const filterNullTimeSlots = (timeSlots: ITimeSlot[]) => {
  return timeSlots.filter((slot: ITimeSlot) => slot.open && slot.close);
};

const isNextDay = (slot: ITimeSlot): boolean => {
  if (!slot.open || !slot.close) return false;
  return slot.close < slot.open;
};

const HoursSelector: React.FC<HoursSelectorProps> = ({
  schedule,
  isEditing,
  onUpdateSchedule,
  validationError,
  disabled = false,
  closedText = "Closed",
  displayEmptyDays = true,
  allowedScheduleBounds,
  allowOverlappingSlots = true,
  readonly = false,
  renderSlotExtras,
}) => {
  const { isMobile } = useViewport();

  const addTimeSlot = useCallback(
    (day: keyof ISchedule) => {
      const daySlots = schedule[day] || [];
      const has24HourSlot = daySlots.some((slot) => is24Hours(slot));
      if (has24HourSlot) {
        return; // Don't add new slot if there's a 24-hour entry
      }
      const newSchedule = {
        ...schedule,
        [day]: [
          ...daySlots,
          {
            id: `${day}-${Date.now()}`,
            open: null,
            close: null,
          },
        ],
      };
      onUpdateSchedule(newSchedule);
    },
    [schedule, onUpdateSchedule]
  );

  const removeTimeSlot = useCallback(
    (day: keyof ISchedule, index: number) => {
      const newSchedule = {
        ...schedule,
        [day]: schedule[day].filter((_, i) => i !== index),
      };
      onUpdateSchedule(newSchedule);
    },
    [schedule, onUpdateSchedule]
  );

  const updateTimeSlot = useCallback(
    (
      day: keyof ISchedule,
      index: number,
      field: "open" | "close",
      value: string
    ) => {
      const newSchedule = {
        ...schedule,
        [day]: schedule[day].map((slot, i) =>
          i === index ? { ...slot, [field]: value } : slot
        ),
      };
      onUpdateSchedule(newSchedule);
    },
    [schedule, onUpdateSchedule]
  );

  // Generic field updater for extra slot data (used by renderSlotExtras)
  const updateSlotField = useCallback(
    (day: keyof ISchedule, index: number, field: string, value: any) => {
      const newSchedule = {
        ...schedule,
        [day]: schedule[day].map((slot, i) =>
          i === index ? { ...(slot as any), [field]: value } : slot
        ),
      } as ISchedule; // cast to preserve base type
      onUpdateSchedule(newSchedule);
    },
    [schedule, onUpdateSchedule]
  );

  return (
    <View className="flex-1 w-full mb-2">
      {validationError && (
        <StyledText className="text-red-500 mt-[-10px] mb-2">
          {validationError}
        </StyledText>
      )}
      <ScrollView className="flex-1 mt-2">
        <View
          className={readonly && !isEditing ? "bg-gray-50 rounded-md p-4" : ""}
        >
          {Object.entries(schedule).map(([day, timeSlots]) => {
            if (
              !isEditing &&
              !displayEmptyDays &&
              filterNullTimeSlots(timeSlots).length === 0
            ) {
              return null;
            }

            const dayOverlapError =
              !allowOverlappingSlots && hasOverlappingSlots(timeSlots);

            return (
              <View
                key={day}
                className="flex flex-row flex-wrap items-start gap-x-4 mb-4 last:mb-0 pt-1"
              >
                <View className="shrink-0 mb-2 w-16 sm:w-24">
                  <StyledText className="font-medium">
                    {isMobile ? formatDayShort(day) : formatDay(day)}
                  </StyledText>
                </View>
                <View className="flex-auto">
                  {!readonly && isEditing ? (
                    <>
                      {displayEmptyDays && timeSlots.length === 0 ? (
                        <View className="flex-row items-center space-x-2 mb-1">
                          <View className="flex p-1 sm:p-2 border rounded-lg bg-[#F2F2F2] shadow-sm border-gray-200 w-24 h-10 mb-1 justify-center cursor-not-allowed">
                            <StyledText className="text-sm text-[#B1B1B1] pl-1">
                              Closed
                            </StyledText>
                          </View>
                          <StyledText className="text-xs text-gray-500">
                            to
                          </StyledText>
                          <View className="flex p-1 sm:p-2 border rounded-lg bg-[#F2F2F2] shadow-sm border-gray-200 w-24 h-10 mb-1 justify-center cursor-not-allowed">
                            <StyledText className="text-sm text-[#B1B1B1] pl-1">
                              Closed
                            </StyledText>
                          </View>
                        </View>
                      ) : (
                        timeSlots.map((slot: ITimeSlot, index: number) => {
                          const dayKey = day as keyof ISchedule;
                          const bounds = allowedScheduleBounds
                            ? allowedScheduleBounds[dayKey]
                            : undefined;
                          const openInvalid =
                            slot.open &&
                            !isTimeInAllowedBounds(slot.open, bounds);
                          const closeInvalid =
                            slot.close &&
                            !isTimeInAllowedBounds(slot.close, bounds);
                          const rowError =
                            openInvalid || closeInvalid
                              ? "Selected time must be within store hours."
                              : null;

                          return (
                            <View key={slot.id}>
                              <View className="flex flex-row items-stretch mb-2 gap-x-3 ml-1">
                                {/* Wrapping content (time inputs + extras) */}
                                <View className="flex flex-row flex-wrap items-center gap-x-3 gap-y-2 flex-1 md:flex-initial">
                                  <View className="flex-row items-center space-x-2">
                                    <TimeInput
                                      time={slot.open}
                                      setTime={(value) =>
                                        updateTimeSlot(
                                          dayKey,
                                          index,
                                          "open",
                                          value
                                        )
                                      }
                                      disabled={disabled}
                                      showInvalid={
                                        !!openInvalid || dayOverlapError
                                      }
                                    />
                                    <StyledText className="text-muted-foreground">
                                      to
                                    </StyledText>
                                    <TimeInput
                                      time={slot.close}
                                      setTime={(value) =>
                                        updateTimeSlot(
                                          dayKey,
                                          index,
                                          "close",
                                          value
                                        )
                                      }
                                      nextDay={isNextDay(slot)}
                                      disabled={disabled}
                                      showInvalid={
                                        !!closeInvalid || dayOverlapError
                                      }
                                    />
                                  </View>
                                  {renderSlotExtras &&
                                    renderSlotExtras({
                                      slot: slot as ITimeSlot & {
                                        [k: string]: any;
                                      },
                                      day: dayKey,
                                      index,
                                      updateSlotField: (field, value) =>
                                        updateSlotField(
                                          dayKey,
                                          index,
                                          field,
                                          value
                                        ),
                                      disabled,
                                      isEditing,
                                    })}
                                </View>
                                {/* Vertically centered delete button */}
                                <View className="justify-center">
                                  <Pressable
                                    className="p-2"
                                    onPress={() =>
                                      removeTimeSlot(dayKey, index)
                                    }
                                    disabled={disabled}
                                  >
                                    <Trash2 size={16} color="#ef4444" />
                                  </Pressable>
                                </View>
                              </View>
                              {rowError && (
                                <StyledText className="text-red-500 text-xs mt-[-6px] mb-2">
                                  {rowError}
                                </StyledText>
                              )}
                            </View>
                          );
                        })
                      )}
                      <View className="flex-row items-center justify-between w-fit mt-1">
                        <Pressable
                          className={`flex-row items-center ${
                            timeSlots.some(is24Hours)
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onPress={() => addTimeSlot(day as keyof ISchedule)}
                          disabled={timeSlots.some(is24Hours) || disabled}
                        >
                          <Plus size={16} className="mr-2" />
                          <StyledText className="text-xs">
                            {timeSlots.some(is24Hours)
                              ? "24-hour slot active"
                              : "Add time slot"}
                          </StyledText>
                        </Pressable>
                      </View>
                    </>
                  ) : filterNullTimeSlots(timeSlots).length > 0 ? (
                    filterNullTimeSlots(timeSlots).map(
                      (slot: ITimeSlot, index: number) => (
                        <View className="flex-row mb-2 gap-4" key={index}>
                          <StyledText key={index} className="text-sm w-40">
                            {is24Hours(slot)
                              ? "Open 24 Hours"
                              : `${formatTime(slot.open)} - ${formatTime(
                                  slot.close
                                )}`}
                          </StyledText>
                          {renderSlotExtras &&
                            renderSlotExtras({
                              slot: slot as ITimeSlot & {
                                [k: string]: any;
                              },
                              day: day as keyof ISchedule,
                              index,
                              updateSlotField: (field, value) =>
                                updateSlotField(
                                  day as keyof ISchedule,
                                  index,
                                  field,
                                  value
                                ),
                              disabled,
                              isEditing,
                            })}
                        </View>
                      )
                    )
                  ) : (
                    <StyledText className="text-sm">{closedText}</StyledText>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

export default HoursSelector;
